import { BigNumber, BigNumberish } from "ethers";
import { useMemo } from "react";
import { useContractRead } from "wagmi";
import { address, config } from "../../../config";
import MultiplierBox from "../../../contracts/MultiplierBox";

type ReadFns =
  | "currentRoundId"
  | "rounds"
  | "roundAmounts"
  | "getPayoutRate"
  | "getRoundNumbers"
  | "getRounds"
  | "getPlayInfosByRoundIds"
  | "getPlayInfos"
  | "getPositions";

// prettier-ignore
type ReadArgs<Fn> = 
  Fn extends "currentRoundId" ? undefined :
  Fn extends "rounds" ? BigNumberish :
  Fn extends "roundAmounts" ? [BigNumberish, string] :
  Fn extends "getPayoutRate" ? [BigNumberish | undefined, string] :
  Fn extends "getRoundNumbers" ? [BigNumberish | undefined, BigNumberish[], string[]] :
  Fn extends "getRounds" ? [BigNumberish[], string[]] :
  Fn extends "getPlayInfosByRoundIds" ? [string, BigNumberish[], BigNumberish, BigNumberish] :
  Fn extends "getPlayInfos" ? [string, BigNumberish, BigNumberish, BigNumberish] :
  Fn extends "getPositions" ? string :
  never;

// prettier-ignore
type ReadResult<Fn> = 
  Fn extends "currentRoundId" ? BigNumber :
  Fn extends "rounds" ? MultiplierBoxRound :
  Fn extends "roundAmounts" ? BigNumber :
  Fn extends "getPayoutRate" ? BigNumber :
  Fn extends "getRoundNumbers" ? BigNumber[] :
  Fn extends "getRounds" ? MultiplierBoxFinishedRound[] :
  Fn extends "getPlayInfosByRoundIds" ? MultiplierBoxPlayInfo[][] :
  Fn extends "getPlayInfos" ? MultiplierBoxPlayInfo[] :
  Fn extends "getPositions" ? MultiplierBoxPosition[] :
  never;

type FnObject<Fn extends ReadFns> = {
  functionName: Fn;
  enabled?: boolean;
  args?: ReadArgs<Fn>;
  watch?: boolean;
};

export type MultiplierBoxRound = {
  roundId: BigNumber;
  startTime: BigNumber;
  closeTime: BigNumber;
  executeAfterTime: BigNumber;
  executedTime: BigNumber;
  answer: BigNumber;
};

export type MultiplierBoxFinishedRound = {
  roundId: BigNumber;
  startTime: BigNumber;
  closeTime: BigNumber;
  executeAfterTime: BigNumber;
  executedTime: BigNumber;
  answer: BigNumber;
  answerPrice: BigNumber;
  answerTime: BigNumber;
  payout: BigNumber;
};

export type MultiplierBoxPlayInfo = {
  game: string;
  amount: BigNumber;
  number: BigNumber;
  claimed: boolean;
};

export type MultiplierBoxPosition = {
  roundId: BigNumber;
  game: string;
};

export function useMultiplierBoxRead<ReadFn extends ReadFns>({
  functionName,
  args,
  watch = false,
  enabled = true,
}: FnObject<ReadFn>) {
  const contractRead = useContractRead({
    args,
    watch,
    enabled,
    functionName,
    addressOrName: address.BB_MAX_ADDRESS,
    contractInterface: MultiplierBox,
    staleTime: config.shortCacheTime,
    cacheTime: config.shortCacheTime,
  });

  const data: ReadResult<ReadFn> | undefined = useMemo(() => {
    if (!contractRead.data) return;
    return contractRead.data as unknown as ReadResult<ReadFn> | undefined;
  }, [contractRead.data]);

  return {
    contractRead,
    data,
  };
}
